import { Locale } from '@superbet-group/web.types';
import { ContactPreference } from '@core/services/authentication/_private/Types';
import { ClosureReason } from '@accountFederation/modules/responsibleGaming/types';
import {
    LicenceState,
    BgcLicence,
} from '@accountFederation/modules/licencePreferences/types';
import { LoginHistoryDto } from '@accountFederation/modules/settings/loginHistory/types';
import { MfaFlow } from '@accountFederation/modules/securityCheck/faceId/mfaFlows';

export interface PrivacyPolicyData {
    generalPrivacy?: boolean;
    customPrivacy?: boolean;
    receiveNews?: boolean;
    customMarketingConsent?: boolean;
    contactPreferences?: ContactPreference[];
}

export interface RegistrationSecondStepParams {
    documentNumber?: string;
    countryId?: string;
    county?: string;
    city?: string;
    address?: string;
    postalCode: string;
    phone: string;
    bankAccountNumber: string;
}

export interface IdentityDataPayload {
    citizenship?: string;
    countryOfBirth?: string;
    documentType?: 'IDENTITY_CARD' | 'PASSPORT';
    documentNumber?: string;
    issuingCountry?: string;
    documentExpiration?: string;
}

export interface LicencePreferencesPayload {
    licences: Partial<Record<BgcLicence, boolean>>;
}

export interface LicencePreferencesResponse {
    data: {
        licences: Partial<Record<BgcLicence, LicenceState>>;
    };
    error: boolean;
}

export type ReasonPayload = {
    reason: ClosureReason;
    otherReason?: string;
};

export type CloseAccountMfaPayload = Partial<{
    password: string;
    username: string;
    // eslint-disable-next-line camelcase
    otp_id: string;
} & ReasonPayload>;

export type VerificationConfigResponse = {
    data: VerificationConfigPayload;
    status: number;
    error: boolean;
};

export type UnicoConfigPayload = {
    provider: VerificationProvider;
    details: {
        metadata: Record<string, unknown>;
    };
    token: string;
    url: string;
    // eslint-disable-next-line camelcase
    otp_id?: string;
};

export type GetIdConfigPayload = {
    provider: VerificationProvider;
    details: {
        locale: 'en';
        metadata: Record<string, unknown>;
        profile: Record<string, unknown>;
        redirects: {
            onComplete?: string;
            onFail?: string;
        };
    };
    external: boolean;
    token: string;
    // eslint-disable-next-line camelcase
    otp_id?: string;
};

export type DevSkipFaceidPayload = {
    // eslint-disable-next-line camelcase
    redirect_url?: 'betler://null-kyc';

    // @deprecated kept for backwards compatibility
    // eslint-disable-next-line camelcase
    magic_url?: 'betler://null-kyc';
};

export type VerificationConfigPayload = (GetIdConfigPayload | UnicoConfigPayload) & DevSkipFaceidPayload;

export const VerificationProvider = {
    Unico: 'unico',
    GetId: 'getid',
} as const;
export type VerificationProvider = typeof VerificationProvider[keyof typeof VerificationProvider];

type IdentityDocument = {
    identifier: string;
    type: 'IDENTITY_CARD' | 'JMBG' | 'CPF';
};

export const SecurityCheckAction = {
    faceid: 'faceid',
    identify: 'identify',
} as const;
export type SecurityCheckAction = typeof SecurityCheckAction[keyof typeof SecurityCheckAction];

// made based on responses from Betler
export type GetPlayerDetailsResponse = {
    data: {
        gender: string;
        firstDepositDate: string | null;
        address: string;
        receiveOffersBonuses: boolean;
        countryId: string | null;
        lastLoginTime: string;
        city: string;
        customPrivacy: boolean;
        messages: unknown[];
        welcomeBonus: boolean;
        testAccount: boolean;
        userUuid: string;
        contactPreferences: ContactPreference[];
        receiveNews: boolean;
        phone: string;
        hasMadeDeposit: boolean;
        showGdprPopup: boolean;
        lastName: string;
        firstName: string;
        dateOfBirth: string;
        customMarketingConsent: boolean;
        userId: number;
        username: string;
        county: string | null;
        shouldVerifySms: boolean;
        sessionId: string;
        registrationDate: string;
        kycDocumentsExpired: boolean;
        kycChecked: boolean;
        email: string;
        postalCode: string | null;
        generalPrivacy: boolean;
        nationality: string | null;
        lastSession?: LastSession; // Betler only
        externalUUID?: string; // iCore/LWB only
        externalGameId?: string; // iCore/LWB only
        locale?: Locale; // Betler only
        isVip?: boolean; // Betler only
        reKycStatus?: ReKycStatus; // iCore only
        identityDocuments?: IdentityDocument[];
        useMfa?: boolean;
    }
    error: boolean;
    errorCode?: string;
    notice: string;
};

interface LastSession {
    country: string
    datetime: string
    ip: string
}

export type SerProPayload = {
    type: 'CPF';
    documentNumber: string;
    dateOfBirth: string;
};

export type SerProResponse = {
    error?: true;
    errors?: Array<{ code: number }>;
    data: {
        name: string;
    };
};

export type LoginHistoryResponse = {
    error?: true;
    data: LoginHistoryDto[],
};

export type VerificationRequestParams = {
    action: typeof SecurityCheckAction['identify'];
} | FaceVerificationRequestParams;

export type AuthFaceVerificationRequestParams = {
    action: typeof SecurityCheckAction.faceid;
    isAnonymous?: false;
    flow: MfaFlow;
};

export type AnonymousFaceVerificationRequestParams = {
    action: typeof SecurityCheckAction.faceid;
    isAnonymous: true;
    flow: MfaFlow;
    email: string;
};

export type FaceVerificationRequestParams = AuthFaceVerificationRequestParams | AnonymousFaceVerificationRequestParams;

export type ReKycStatus = 'PENDING' | 'FAILED' | 'EXPIRED' | 'OK';
